import React, { useEffect, useState, useCallback } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import api from '../api';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, BarChart, Bar } from 'recharts';

const COLORS = ['#4caf50', '#ff9800', '#9e9e9e', '#2196f3', '#9c27b0', '#f44336', '#03a9f4', '#ffc107', '#795548', '#607d8b', '#8bc34a', '#e91e63'];

export default function AdminDashboard() {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [highlights, setHighlights] = useState(null);
    const [distribution, setDistribution] = useState([]);
    const [schoolProfileStats, setSchoolProfileStats] = useState({});
    const [cigStats, setCigStats] = useState({});
    const [ilgStats, setIlgStats] = useState({});
    const [schoolProfileDistribution, setSchoolProfileDistribution] = useState([]);
    const [cigDistribution, setCigDistribution] = useState([]);
    const [ilgDistribution, setIlgDistribution] = useState([]);
    const [stateDistribution, setStateDistribution] = useState([]);
    const [signups, setSignups] = useState([]);
    const [completionFunnel, setCompletionFunnel] = useState([]);
    const [excludeDQ, setExcludeDQ] = useState(false);
    const [avgSchoolProfileTime, setAvgSchoolProfileTime] = useState(null);
    const [avgCigTime, setAvgCigTime] = useState(null);
    const [avgIlgTime, setAvgIlgTime] = useState(null);
    const [conferenceDistribution, setConferenceDistribution] = useState([]);

    const toStatusPieData = (stats) => ([
        { name: 'Completed', value: Number(stats?.completed_count ?? 0) },
        { name: 'Draft', value: Number(stats?.draft_count ?? 0) },
        { name: 'Not Started', value: Number(stats?.not_started_count ?? 0) },
    ]);

    const formatHighlight = (item) => {
        if (!item) return '—';
        const schoolName = item.school_name || item.schoolName || 'Unknown school';
        const completedAt = item.completed_at || item.completedAt || item.day || '—';
        const count = item.completed_count ?? item.count;
        const formatDate = (value) => {
            if (!value || value === '—') return '—';
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            const year = parsed.getFullYear();
            return `${month}-${day}-${year}`;
        };
        if (item.day) {
            return `${formatDate(completedAt)}${count !== undefined && count !== null ? ` (${count})` : ''}`;
        }
        if (count !== undefined && count !== null) {
            return `${schoolName} - ${formatDate(completedAt)}${item.day ? ` (${count})` : ''}`;
        }
        return `${schoolName} - ${formatDate(completedAt)}`;
    };

    const formatEasternTimestamp = (value) => {
        if (!value || value === '—') return '—';
        const raw = String(value).trim();
        const normalized = raw.replace('T', ' ').replace(/\.\d+$/, '');
        const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/);
        if (!match) return raw;
        const [, year, month, day, hourPart, minutePart] = match;
        if (!hourPart || !minutePart) return `${month}-${day}-${year} ET`;
        const hour = Number(hourPart);
        const minute = minutePart;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${month}-${day}-${year} ${hour12}:${minute} ${ampm} ET`;
    };

    const formatSubmissionHighlight = (item) => {
        if (!item) return '—';
        const schoolName = item.school_name || item.schoolName || 'Unknown school';
        const completedAt = item.completed_at || item.completedAt || '—';
        return `${schoolName} - ${formatEasternTimestamp(completedAt)}`;
    };

    const load = useCallback(async (dqOverride) => {
        setLoading(true);
        const dq = dqOverride !== undefined ? dqOverride : excludeDQ;
        const dqParam = dq ? '&excludeDQ=true' : '';
        try {
            const [sRes, hRes, distRes, suRes, stRes, formDistRes, formAvgRes, confRes] = await Promise.all([
                api.get(`/admin/api/schools/dashboard/summary?excludeDQ=${dq ? 'true' : 'false'}`),
                api.get(`/admin/api/schools/dashboard/highlights?excludeDQ=${dq ? 'true' : 'false'}`),
                api.get(`/admin/api/schools/dashboard/status-distribution?excludeDQ=${dq ? 'true' : 'false'}`),
                api.get(`/admin/api/schools/dashboard/signups?days=30${dqParam}`),
                api.get(`/admin/api/schools/dashboard/state-distribution?excludeDQ=${dq ? 'true' : 'false'}`),
                api.get(`/admin/api/schools/dashboard/form-status-distribution?excludeDQ=${dq ? 'true' : 'false'}`),
                api.get(`/admin/api/schools/dashboard/average-form-completion?excludeDQ=${dq ? 'true' : 'false'}`),
                api.get(`/admin/api/schools/dashboard/conference-distribution?excludeDQ=${dq ? 'true' : 'false'}`),
            ]);

            if (sRes.status === 403) throw new Error('forbidden');

            const sJson = sRes.data;
            const hJson = hRes.data ?? {};
            const distJson = distRes.data ?? null;
            const suJson = suRes.data ?? [];
            const stJson = stRes.data ?? [];
            const formDistJson = formDistRes.data ?? {};
            const formAvgJson = formAvgRes.data ?? {};
            const confJson = confRes.data ?? [];

            setSummary(sJson);
            setHighlights(hJson);

            setDistribution([
                { name: 'Completed', value: sJson?.completedCount || 0 },
                { name: 'Draft', value: sJson?.draftCount || 0 },
                { name: 'Not Started', value: sJson?.notStartedCount || 0 },
            ]);

            setStateDistribution((stJson || []).map(r => ({ name: r.state || 'Unknown', value: Number(r.cnt) })));
            setSchoolProfileStats(formDistJson.schoolProfile || {});
            setCigStats(formDistJson.cig || {});
            setIlgStats(formDistJson.ilg || {});
            setSchoolProfileDistribution(toStatusPieData(formDistJson.schoolProfile));
            setCigDistribution(toStatusPieData(formDistJson.cig));
            setIlgDistribution(toStatusPieData(formDistJson.ilg));

            setAvgSchoolProfileTime(formAvgJson.schoolProfile || {});
            setAvgCigTime(formAvgJson.cig || {});
            setAvgIlgTime(formAvgJson.ilg || {});

            setCompletionFunnel([
                { name: 'School Profile Completed', value: formDistJson.schoolProfile?.completed_count ?? 0 },
                { name: 'CIG Completed', value: formDistJson.cig?.completed_count ?? 0 },
                { name: 'ILG Completed', value: formDistJson.ilg?.completed_count ?? 0 },
            ]);

            setSignups((suJson || []).map(r => ({ date: r.day, count: Number(r.cnt) })));

            const conferenceRows = (confJson || []).map(r => ({
                name: r.conference || 'Unknown',
                value: Number(r.count),
            }));
            const topConferences = conferenceRows.slice(0, 10);
            const otherCount = conferenceRows.slice(10).reduce((sum, row) => sum + row.value, 0);
            setConferenceDistribution(otherCount > 0
                ? [...topConferences, { name: 'Other', value: otherCount }]
                : topConferences);

        } catch (err) {
            if (err.message === 'forbidden') {
                console.error('Dashboard API returned 403 — check authentication token.');
            } else {
                console.error(err);
            }
        } finally {
            setLoading(false);
        }
    }, [excludeDQ]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

    const conferenceChartHeight = {
        xs: Math.max(420, conferenceDistribution.length * 30),
        md: Math.max(520, conferenceDistribution.length * 32),
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 3 }}>
            {/* Left-aligned content wrapper so content sits after the sidebar */}
            <Box sx={{ width: { xs: '100%', md: '1200px' }, ml: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ textAlign: 'left' }}>Admin Dashboard</Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={() => load()}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <FormControlLabel
                        control={<Switch checked={excludeDQ} onChange={e => { setExcludeDQ(e.target.checked); load(e.target.checked); }} color="primary" />}
                        label="Exclude Disqualified Schools"
                        sx={{ ml: 2 }}
                    />
                </Box>

                {/* Top cards row (flex, equal widths) */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Box sx={{ flex: '1 1 33%', maxWidth: { xs: '100%', md: '33%' } }}>
                        <Card sx={{ height: '100%', minHeight: 120 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>School Profile Completed</Typography>
                                <Typography component="h1" variant="h1" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>{schoolProfileStats?.completed_count ?? 0}</Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 33%', maxWidth: { xs: '100%', md: '33%' } }}>
                        <Card sx={{ height: '100%', minHeight: 120 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>CIG Completed</Typography>
                                <Typography component="h1" variant="h1" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>{cigStats?.completed_count ?? 0}</Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 33%', maxWidth: { xs: '100%', md: '33%' } }}>
                        <Card sx={{ height: '100%', minHeight: 120 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>ILG Completed</Typography>
                                <Typography component="h1" variant="h1" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' } }}>{ilgStats?.completed_count ?? 0}</Typography>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>

                {/* Closed Application Highlights */}
                <Box sx={{ mb: 3 }}>
                    <Card sx={{ width: '100%' }}>
                        <CardContent>
                            <Typography component="h3" variant="h6" sx={{ mb: 2, textAlign: 'center' }}>Closed Application Highlights</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: '1 1 520px', width: '100%', maxWidth: 560 }}>
                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                                        <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>CIG Quick Stats</Typography>
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>First submitted:</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>{formatSubmissionHighlight(highlights?.firstCig)}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Last submitted:</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>{formatSubmissionHighlight(highlights?.lastCig)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Day with most completions:</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>{formatHighlight(highlights?.topCigDay)}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: '1 1 520px', width: '100%', maxWidth: 560 }}>
                                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                                        <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>ILG Quick Stats</Typography>
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>First submitted:</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>{formatSubmissionHighlight(highlights?.firstIlg)}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Last submitted:</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>{formatSubmissionHighlight(highlights?.lastIlg)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Day with most completions:</Typography>
                                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>{formatHighlight(highlights?.topIlgDay)}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Signups - full width (30 days) */}
                <Box sx={{ mb: 2 }}>
                    <Card sx={{ width: '100%' }}>
                        <CardContent sx={{ px: 0 }}>
                            <Box sx={{ px: 2, pt: 1 }}>
                                <Typography component="h3" variant="h6" sx={{ mb: 1 }}>Signups (last 30 days)</Typography>
                            </Box>
                            <Box sx={{ width: '100%', height: { xs: 260, md: 320 } }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={signups} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                        <XAxis dataKey="date" tickMargin={18} height={60} />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ marginTop: 16 }} />
                                        <Bar dataKey="count" fill="#1976d2" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2} justifyContent="center">
                        <Grid item xs={12} md="auto">
                            <Card sx={{ width: { xs: '100%', md: 360 } }}>
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>School Profile Status</Typography>
                                    <Box sx={{ width: '100%', height: { xs: 360, md: 460 } }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={schoolProfileDistribution} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius="72%" labelLine={false} label>
                                                    {schoolProfileDistribution.map((entry, index) => (
                                                        <Cell key={`cell-school-profile-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Legend verticalAlign="bottom" height={88} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md="auto">
                            <Card sx={{ width: { xs: '100%', md: 360 } }}>
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>CIG Status</Typography>
                                    <Box sx={{ width: '100%', height: { xs: 360, md: 460 } }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={cigDistribution} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius="72%" labelLine={false} label>
                                                    {cigDistribution.map((entry, index) => (
                                                        <Cell key={`cell-cig-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Legend verticalAlign="bottom" height={88} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md="auto">
                            <Card sx={{ width: { xs: '100%', md: 360 } }}>
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>ILG Status</Typography>
                                    <Box sx={{ width: '100%', height: { xs: 360, md: 460 } }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={ilgDistribution} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius="72%" labelLine={false} label>
                                                    {ilgDistribution.map((entry, index) => (
                                                        <Cell key={`cell-ilg-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Legend verticalAlign="bottom" height={88} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Completion Funnel and State Distribution - Full width side by side */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Box sx={{ flex: '1 1 50%' }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography component="h3" variant="h6" sx={{ mb: 2, textAlign: 'center' }}>Completion Funnel</Typography>
                                <Box sx={{ width: '100%', height: { xs: 300, md: 420 } }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={completionFunnel} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="value" fill="#1976d2" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 50%' }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography component="h3" variant="h6" sx={{ mb: 1, textAlign: 'center' }}>State Distribution</Typography>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', height: { xs: 300, md: 420 } }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={stateDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="70%" labelLine={false} label>
                                                {stateDistribution.map((entry, index) => (
                                                    <Cell key={`cell-state-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Legend verticalAlign="bottom" height={80} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>

                {/* Athletic Conferences - full width horizontal bar chart */}
                <Box sx={{ mb: 2 }}>
                    <Card sx={{ width: '100%' }}>
                        <CardContent>
                            <Typography component="h3" variant="h6" sx={{ mb: 2, textAlign: 'center' }}>Athletic Conference Distribution - Top 10</Typography>
                            <Box sx={{ width: '100%', height: conferenceChartHeight }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={conferenceDistribution} layout="vertical" margin={{ top: 10, right: 20, left: 30, bottom: 10 }}>
                                        <XAxis type="number" />
                                        <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={420} interval={0} />
                                        <Tooltip />
                                        <Bar dataKey="value" fill="#1976d2" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
}
